import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { HTTP_METHODS, invokeApi } from "../../utils/http-service";
import { CF_APPROVAL_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";
import { exportToExcel } from "../../../common-utilities/utils";
import { toast } from "react-toastify";
import "./index.scss";

const REGIONS = ["North", "West", "South", "East"];

const ROW_GROUPS = [
  {
    type: "section",
    label: "TARGETS",
    bg: "#ffffff",
  },
  {
    key: "aopTarget",
    label: "AOP Target",
    fontWeight: "bold",
  },
  {
    key: "target",
    label: "Target",
    fontWeight: "bold",
  },
  {
    type: "section",
    label: "CATCHMENT PIPELINE",
    bg: "#ffffff",
  },
  { key: "carryForward", label: "Carry Forward" },
  { key: "aopCatchment", label: "AOP Catchment" },
  { key: "outsideAOP", label: "Outside AOP" },
  {
    key: "totalCatchments",
    label: "Total Catchments",
    bg: "#ffffff",
    fontWeight: "bold",
  },
  { key: "lead", label: "Lead" },
  { key: "cfUD", label: "Under discussion" },
  { key: "cfApproved", label: "Approved" },
  { key: "underConstruction", label: "Under Construction" },
  { key: "opened", label: "Opened" },
  {
    key: "balancePipeline",
    label: "Balance Pipeline",
    bg: "#ffffff",
    fontWeight: "bold",
  },
  { key: "dropped", label: "Dropped" },
];

const cellSx = {
  border: "1px solid #bdbdbd",
  padding: "6px 10px",
  fontSize: "0.82rem",
};

const REPORT_LOGIC_ITEMS = [
  { label: "Date window", text: "All counts (other than Carry Forward) are scoped to records created between the selected fiscal year's Date From and Date To." },
  { label: "AOP Target / Target", text: "Fixed per-region values entered in the fiscal year's Report Config (Setup)." },
  { label: "Carry Forward", text: "Records created before this quarter's Date From that were still unresolved as of this quarter's Date To — i.e. not yet Opened or Dropped by then. A record only leaves Carry Forward starting the quarter it actually opens or gets dropped." },
  { label: "AOP Catchment", text: "Fixed per-region value from the Report Config." },
  { label: "Outside AOP", text: "Records created in this fiscal year's window with partOfAOP = false." },
  { label: "Total Catchments", text: "Carry Forward + AOP Catchment + Outside AOP." },
  { label: "Lead / Under discussion / Approved / Under Construction", text: "Records with status = In Progress whose current step maps to that stage (via Step Master's stage classification, not the record's own status field)." },
  { label: "Opened", text: "Records with status = Opened." },
  { label: "Balance Pipeline", text: "Total Catchments − (Lead + Under discussion + Approved + Under Construction + Opened). Dropped records are excluded from this subtraction and tracked separately." },
  { label: "Dropped", text: "Records with status = Dropped." },
];

export const CfV2PipelineDashboard = () => {
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    invokeApi(HTTP_METHODS.GET, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_REPORT_CONFIG}`)
      .then((res) => {
        const fys = (Array.isArray(res) ? res : [res]).map((c) => c.fiscalYear).filter(Boolean);
        setFiscalYears(fys);
        if (fys.length > 0) setSelectedFY(fys[0]);
      })
      .catch(() => toast.error("Failed to load fiscal year configs"));
  }, []);

  useEffect(() => {
    if (!selectedFY) return;
    setLoading(true);
    invokeApi(
      HTTP_METHODS.GET,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_PIPELINE_REPORT}`,
      null,
      { fiscalYear: selectedFY }
    )
      .then((res) => setReport(res))
      .catch(() => toast.error("Failed to load pipeline report"))
      .finally(() => setLoading(false));
  }, [selectedFY]);

  const getValue = (rowKey, region) => {
    if (!report?.rows?.[rowKey]) return 0;
    return report.rows[rowKey][region] ?? 0;
  };

  const downloadReport = () => {
    if (!report) return;
    const data = ROW_GROUPS.map((row) => {
      if (row.type === "section") {
        const entry = { Status: row.label };
        REGIONS.forEach((r) => { entry[r] = ""; });
        entry[selectedFY] = "";
        return entry;
      }
      const entry = { Status: row.label };
      REGIONS.forEach((r) => { entry[r] = getValue(row.key, r); });
      entry[selectedFY] = report.rows?.[row.key]?.total ?? 0;
      return entry;
    });
    exportToExcel(data, `Pipeline_Report_${selectedFY}`);
  };

  return (
    <Box className="pipeline-report-container">
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Pipeline Tracker
        </Typography>
        <Select
          size="small"
          value={selectedFY}
          onChange={(e) => setSelectedFY(e.target.value)}
          displayEmpty
          sx={{ minWidth: 120 }}
        >
          {fiscalYears.map((fy) => (
            <MenuItem key={fy} value={fy}>
              {fy}
            </MenuItem>
          ))}
        </Select>
        {report && (
          <Tooltip title="Download Report">
            <IconButton size="small" onClick={downloadReport}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip
          title={
            <Box sx={{ p: 0.5 }}>
              <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                How this report is calculated
              </Typography>
              {REPORT_LOGIC_ITEMS.map((item) => (
                <Typography key={item.label} variant="caption" display="block" sx={{ mb: 0.5 }}>
                  <b>{item.label}:</b> {item.text}
                </Typography>
              ))}
            </Box>
          }
          slotProps={{ tooltip: { sx: { maxWidth: 420 } } }}
        >
          <IconButton size="small">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : !report ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          {fiscalYears.length === 0
            ? "No fiscal year configs found. Please add a config in Setup."
            : "Select a fiscal year to view the pipeline report."}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" sx={{ width: "auto", minWidth: 560 }}>
            <TableHead>
              <TableRow sx={{ background: "#008a52" }}>
                <TableCell sx={{ ...cellSx, color: "#fff", fontWeight: "bold", minWidth: 200 }}>
                  Status
                </TableCell>
                {REGIONS.map((r) => (
                  <TableCell key={r} sx={{ ...cellSx, color: "#fff", fontWeight: "bold", textAlign: "center", minWidth: 80 }}>
                    {r}
                  </TableCell>
                ))}
                <TableCell sx={{ ...cellSx, color: "#fff", fontWeight: "bold", textAlign: "center", minWidth: 80 }}>
                  {selectedFY}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ROW_GROUPS.map((row, idx) => {
                if (row.type === "section") {
                  return (
                    <TableRow key={idx} sx={{ background: row.bg || "#eeeeee" }}>
                      <TableCell
                        colSpan={REGIONS.length + 2}
                        sx={{ ...cellSx, fontWeight: "bold", fontSize: "0.8rem", letterSpacing: "0.05em" }}
                      >
                        {row.label}
                      </TableCell>
                    </TableRow>
                  );
                }
                const rowBg = row.bg || "inherit";
                const total = report.rows?.[row.key]?.total ?? 0;
                return (
                  <TableRow key={row.key} sx={{ background: rowBg }}>
                    <TableCell
                      sx={{
                        ...cellSx,
                        color: row.color || "inherit",
                        fontWeight: row.fontWeight || "normal",
                      }}
                    >
                      {row.label}
                    </TableCell>
                    {REGIONS.map((r) => (
                      <TableCell
                        key={r}
                        sx={{
                          ...cellSx,
                          textAlign: "center",
                          fontWeight: row.fontWeight || "normal",
                          color: row.color || "inherit",
                        }}
                      >
                        {getValue(row.key, r)}
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        ...cellSx,
                        textAlign: "center",
                        fontWeight: "bold",
                        color: row.color || "inherit",
                        background: rowBg !== "inherit" ? rowBg : "#f5f5f5",
                      }}
                    >
                      {total}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
